/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import {
    Link as RouterLink,
    NavLink as RouterNavLink,
    type LinkProps as RouterLinkProps,
    type NavLinkProps as RouterNavLinkProps,
    type To,
} from 'react-router';
import { buildUrl, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import { appendSuffix, findLegacyRoute } from '@/middlewares/legacy-routes';

/** Resolved navigation target plus whether it must bypass client-side routing. */
type ResolvedTarget = { to: To; reloadDocument: boolean };

/**
 * Resolve a `<Link to>` for the current site context, and decide whether the navigation must be a
 * full document load.
 *
 * For legacy routes (hybrid mode), the href is the locale-PREFIXED path (via `buildUrl`), not the
 * bare functional path. On the production eCDN the path is forwarded VERBATIM to SFRA, and SFRA
 * resolves locale from the path. A bare `/cart` 404s / renders the default locale; `/uk/cart`
 * resolves correctly.
 */
function useResolvedTarget(to: To): ResolvedTarget {
    const { site } = useSite();
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    if (typeof to !== 'string' || !site) {
        return { to, reloadDocument: false };
    }

    const hybrid = config.hybrid;
    if (hybrid?.enabled && hybrid.legacyRoutes?.length) {
        const queryHashStart = to.search(/[?#]/);
        const pathPart = queryHashStart === -1 ? to : to.slice(0, queryHashStart);
        const queryHash = queryHashStart === -1 ? '' : to.slice(queryHashStart);

        const matchedRoute = findLegacyRoute(pathPart, hybrid.legacyRoutes);
        if (matchedRoute) {
            return {
                to: buildUrl({
                    to: `${appendSuffix(pathPart, matchedRoute.suffix)}${queryHash}`,
                    urlConfig: config.url,
                    params: { siteId: siteRef, localeId: localeRef },
                }),
                reloadDocument: true,
            };
        }
    }

    return {
        to: buildUrl({ to, urlConfig: config.url, params: { siteId: siteRef, localeId: localeRef } }),
        reloadDocument: false,
    };
}

export const Link = forwardRef<HTMLAnchorElement, RouterLinkProps>(function Link({ to: _to, ...rest }, ref) {
    const { to, reloadDocument } = useResolvedTarget(_to);
    return <RouterLink ref={ref} to={to} reloadDocument={reloadDocument} {...rest} />;
});

export const NavLink = forwardRef<HTMLAnchorElement, RouterNavLinkProps>(function NavLink({ to: _to, ...rest }, ref) {
    const { to, reloadDocument } = useResolvedTarget(_to);
    return <RouterNavLink ref={ref} to={to} reloadDocument={reloadDocument} {...rest} />;
});
